/**
 * BookStrip — a small "Related reading" widget.
 * Used inside /learn lessons and on the calculator results panel.
 *
 * Filters by category, shows up to 3 covers with title/author + tracked links.
 * Renders nothing while loading or if 0 books match — never looks apologetic.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookOpen } from "lucide-react";
import { fetchBooks, trackBookClick } from "@/lib/api";
import { track, EVENTS } from "@/lib/analytics";

export default function BookStrip({ category, title = "Related reading", limit = 3 }) {
  const [books, setBooks] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchBooks(category)
      .then((d) => setBooks((d.books || []).slice(0, limit)))
      .catch(() => setBooks([]));
  }, [category, limit]);

  if (!books || books.length === 0) return null;

  const click = async (e, b) => {
    e.preventDefault();
    track(EVENTS.BOOK_CLICK, { book_id: b.book_id, is_centennial: !!b.is_centennial, surface: `strip:${category || "all"}` });
    if (b.is_centennial) {
      navigate("/ebook/100-year-retirement-plan");
      return;
    }
    try {
      const { url } = await trackBookClick(b.book_id);
      track(EVENTS.BOOK_PURCHASE_OUTBOUND, { book_id: b.book_id, surface: `strip:${category || "all"}` });
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      /* silent fail — books are optional */
    }
  };

  return (
    <div data-testid={`bookstrip-${category || "all"}`} className="mt-8 pt-6 border-t border-[#E5E2DC]">
      <div className="flex items-center justify-between mb-4">
        <div className="inline-flex items-center gap-2">
          <BookOpen size={14} className="text-[#5C6B62]" />
          <span className="text-[10px] tracking-[0.22em] uppercase text-[#5C6B62] font-semibold">
            {title}
          </span>
        </div>
        <Link to="/books" className="text-xs text-[#2B5339] hover:underline">
          See all →
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {books.map((b) => (
          <a
            key={b.book_id}
            data-testid={`book-${b.book_id}`}
            href="#"
            onClick={(e) => click(e, b)}
            className="group flex gap-3 bg-[#F9F8F6] border border-[#E5E2DC] rounded-xl p-3 hover:border-[#2B5339] hover:shadow-sm transition-all"
          >
            {b.cover_url ? (
              <img
                src={b.cover_url}
                alt={b.title}
                className="w-14 h-20 object-cover rounded-md shadow-sm flex-shrink-0"
                onError={(e) => {
                  if (!e.target.dataset.fallback) {
                    e.target.dataset.fallback = "1";
                    e.target.src = "/book-placeholder.svg";
                  }
                }}
              />
            ) : (
              <div className="w-14 h-20 bg-[#E5E2DC] rounded-md flex-shrink-0" />
            )}
            <div className="min-w-0">
              <div className="text-sm font-medium text-[#1C2B23] leading-tight line-clamp-2 group-hover:text-[#2B5339]">
                {b.title}
              </div>
              <div className="text-[11px] text-[#5C6B62] mt-1">{b.author}</div>
              {b.price_hint && (
                <div className="text-[11px] text-[#2B5339] mt-1.5 font-medium">{b.price_hint}</div>
              )}
            </div>
          </a>
        ))}
      </div>
      <p className="text-[10px] text-[#5C6B62] mt-3 italic">
        Affiliate links · Centennial earns a small commission at no extra cost to you.
      </p>
    </div>
  );
}
